import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Settings, Sun, Moon, Zap, Users, Save, RefreshCw, 
  AlertTriangle, Smartphone, Monitor, ClipboardList, Send, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import AttractSlidesManager from './AttractSlidesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemSettingsPanel = () => {
  const [settings, setSettings] = useState({
    temporada_alta: false,
    allow_manual_orders: true,
    auto_dark_mode: true,
    dark_mode_start: '20:00',
    dark_mode_end: '07:00',
    order_mode: 'direct_send'
  });
  const [kiosk, setKiosk] = useState({ enabled: false, idle_minutes: 5 });
  const [kioskSaving, setKioskSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const [respSys, respKiosk] = await Promise.all([
        fetch(`${API_URL}/api/admin/system-settings`),
        fetch(`${API_URL}/api/kiosk/config`),
      ]);
      if (respSys.ok) {
        const data = await respSys.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
      if (respKiosk.ok) {
        const k = await respKiosk.json();
        setKiosk({
          enabled: !!k.enabled,
          idle_minutes: Math.max(1, Math.min(120, Number(k.idle_minutes) || 5)),
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`${API_URL}/api/admin/system-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Configuración guardada correctamente');
      } else {
        toast.error('Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveKiosk = async (next) => {
    setKioskSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${API_URL}/api/kiosk/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(next),
      });
      if (resp.ok) {
        setKiosk(next);
        toast.success(next.enabled ? 'Modo Kiosko activado' : 'Modo Kiosko desactivado');
      } else {
        toast.error('No se pudo actualizar el Modo Kiosko');
      }
    } catch {
      toast.error('Error de red al guardar Modo Kiosko');
    } finally {
      setKioskSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h2>
          <p className="text-gray-500">Ajustes globales del restaurante</p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-green-600 hover:bg-green-700">
          {saving ? (
            <RefreshCw className="animate-spin mr-2" size={16} />
          ) : (
            <Save size={16} className="mr-2" />
          )}
          Guardar cambios
        </Button>
      </div>

      {/* Temporada Alta */}
      <Card className={settings.temporada_alta ? 'border-amber-300 bg-amber-50/50' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${settings.temporada_alta ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Zap className={settings.temporada_alta ? 'text-amber-600' : 'text-gray-600'} size={24} />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Modo Temporada Alta
                  {settings.temporada_alta && (
                    <Badge className="bg-amber-500">ACTIVO</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Fuerza pedidos exclusivamente por carta digital
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.temporada_alta}
              onCheckedChange={() => handleToggle('temporada_alta')}
            />
          </div>
        </CardHeader>
        <CardContent>
          {settings.temporada_alta ? (
            <div className="p-4 bg-amber-100 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">Modo Temporada Alta activado</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Los clientes SOLO pueden hacer pedidos escaneando el QR de la mesa. 
                    No se permiten pedidos verbales en barra. Ideal para momentos de alta demanda.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Cuando está desactivado, los clientes pueden pedir tanto por la carta digital como verbalmente al personal.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pedidos Manuales */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={24} />
              </div>
              <div>
                <CardTitle>Pedidos Manuales</CardTitle>
                <CardDescription>
                  Permitir al personal crear pedidos desde el panel admin
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.allow_manual_orders}
              onCheckedChange={() => handleToggle('allow_manual_orders')}
            />
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            {settings.allow_manual_orders 
              ? 'El personal puede crear pedidos manualmente desde "Nuevo Pedido" en el panel de administración.'
              : 'Solo se aceptan pedidos a través de la carta digital.'}
          </p>
        </CardContent>
      </Card>

      {/* Modo de Pedido del Cliente */}
      <Card className={settings.order_mode === 'waiter_only' ? 'border-orange-300 bg-orange-50/50' : 'border-green-300 bg-green-50/50'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${settings.order_mode === 'waiter_only' ? 'bg-orange-100' : 'bg-green-100'}`}>
                {settings.order_mode === 'waiter_only' ? (
                  <ClipboardList className="text-orange-600" size={24} />
                ) : (
                  <Send className="text-green-600" size={24} />
                )}
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Modo de Pedido del Cliente
                  <Badge className={settings.order_mode === 'waiter_only' ? 'bg-orange-500' : 'bg-green-500'}>
                    {settings.order_mode === 'waiter_only' ? 'LISTA' : 'ENVÍO DIRECTO'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Cómo pueden los clientes realizar sus pedidos
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.order_mode === 'direct_send'}
              onCheckedChange={() => setSettings(prev => ({ 
                ...prev, 
                order_mode: prev.order_mode === 'direct_send' ? 'waiter_only' : 'direct_send' 
              }))}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                settings.order_mode === 'waiter_only' 
                  ? 'border-orange-400 bg-orange-100' 
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
              onClick={() => setSettings(prev => ({ ...prev, order_mode: 'waiter_only' }))}
            >
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList size={18} className={settings.order_mode === 'waiter_only' ? 'text-orange-600' : 'text-gray-400'} />
                <span className={`font-semibold ${settings.order_mode === 'waiter_only' ? 'text-orange-800' : 'text-gray-500'}`}>
                  Solo Lista para Camarero
                </span>
              </div>
              <p className="text-sm text-gray-600">
                El cliente crea su lista de productos y se la muestra al camarero para confirmar el pedido verbalmente.
              </p>
              <div className="mt-3 text-xs text-gray-500">
                ✓ Más control sobre los pedidos<br/>
                ✓ Interacción personal con el cliente<br/>
                ✓ Ideal para servicio tradicional
              </div>
            </div>
            
            <div 
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                settings.order_mode === 'direct_send' 
                  ? 'border-green-400 bg-green-100' 
                  : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
              onClick={() => setSettings(prev => ({ ...prev, order_mode: 'direct_send' }))}
            >
              <div className="flex items-center gap-2 mb-2">
                <Send size={18} className={settings.order_mode === 'direct_send' ? 'text-green-600' : 'text-gray-400'} />
                <span className={`font-semibold ${settings.order_mode === 'direct_send' ? 'text-green-800' : 'text-gray-500'}`}>
                  Envío Directo a Cocina
                </span>
              </div>
              <p className="text-sm text-gray-600">
                El cliente puede enviar su pedido directamente al servicio desde su móvil (requiere QR de mesa).
              </p>
              <div className="mt-3 text-xs text-gray-500">
                ✓ Más rapidez en momentos de alta demanda<br/>
                ✓ Menos carga para el personal<br/>
                ✓ Ideal para temporada alta
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dark Mode */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Moon className="text-purple-600" size={24} />
              </div>
              <div>
                <CardTitle>Modo Oscuro Automático</CardTitle>
                <CardDescription>
                  Activa el modo noche según el horario configurado
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.auto_dark_mode}
              onCheckedChange={() => handleToggle('auto_dark_mode')}
            />
          </div>
        </CardHeader>
        <CardContent>
          {settings.auto_dark_mode && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-sm text-gray-600 mb-2 block">
                  <Moon size={14} className="inline mr-1" />
                  Inicio modo noche
                </Label>
                <Input
                  type="time"
                  value={settings.dark_mode_start}
                  onChange={(e) => setSettings(prev => ({ ...prev, dark_mode_start: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-2 block">
                  <Sun size={14} className="inline mr-1" />
                  Fin modo noche
                </Label>
                <Input
                  type="time"
                  value={settings.dark_mode_end}
                  onChange={(e) => setSettings(prev => ({ ...prev, dark_mode_end: e.target.value }))}
                />
              </div>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-4">
            {settings.auto_dark_mode 
              ? `El modo oscuro se activará automáticamente de ${settings.dark_mode_start} a ${settings.dark_mode_end}.`
              : 'El modo oscuro solo se activará manualmente por el usuario.'}
          </p>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone size={20} />
            Experiencia del Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Monitor size={16} />
                <span className="font-medium">Sin QR (entrada directa)</span>
              </div>
              <p className="text-sm text-gray-600">
                {settings.temporada_alta 
                  ? '❌ Se mostrará mensaje para escanear QR de mesa'
                  : '✅ Puede ver menú y mostrar selección al camarero'}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone size={16} className="text-green-600" />
                <span className="font-medium text-green-800">Con QR de mesa</span>
              </div>
              <p className="text-sm text-green-700">
                ✅ Puede hacer pedidos directamente a cocina/barra
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Modo Kiosko ─────────────────────────────────────────────── */}
      <Card className={kiosk.enabled ? 'border-stone-400 bg-stone-50/60' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${kiosk.enabled ? 'bg-stone-200' : 'bg-gray-100'}`}>
                <Lock className={kiosk.enabled ? 'text-stone-700' : 'text-gray-600'} size={24} />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Modo Kiosko
                  {kiosk.enabled && <Badge className="bg-stone-600">ACTIVO</Badge>}
                </CardTitle>
                <CardDescription>
                  Tras X minutos sin actividad, la app vuelve a la pantalla de idiomas.
                  Salir requiere PIN (091764, 5 toques en el logo PONT 10).
                </CardDescription>
              </div>
            </div>
            <Switch
              data-testid="kiosk-mode-toggle"
              checked={kiosk.enabled}
              disabled={kioskSaving}
              onCheckedChange={(v) => saveKiosk({ ...kiosk, enabled: v })}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 max-w-md">
            <Label htmlFor="kiosk-idle" className="whitespace-nowrap">
              Minutos sin actividad:
            </Label>
            <Input
              id="kiosk-idle"
              data-testid="kiosk-idle-input"
              type="number"
              min={1}
              max={120}
              value={kiosk.idle_minutes}
              onChange={(e) => setKiosk(prev => ({
                ...prev,
                idle_minutes: Math.max(1, Math.min(120, Number(e.target.value) || 5)),
              }))}
              onBlur={() => saveKiosk(kiosk)}
              className="w-24"
            />
            <span className="text-sm text-gray-500">(1–120)</span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            También puedes activarlo en una tablet concreta añadiendo <code>?kiosk=1</code> a la URL.
          </p>
        </CardContent>
      </Card>

      <AttractSlidesManager />
    </div>
  );
};

export default SystemSettingsPanel;
